import React, { ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { InternalFieldName } from 'react-hook-form/dist/types/fields';
import { IMaskInput } from 'react-imask';
import PersonType from '../../enums/PersonType';
import Form from '../Crud/Form';
import {
  getIdentiferMask,
  removeMask,
  toIdentifierMask,
} from '../../services/mask';
import { isCnpjValid, isCpfValid } from '../../services/cpfCnpj';

export interface CustomerFormFields {
  type: PersonType;
  identifier: string;
  name: string;
  alias?: string;
  useAlias: boolean;
  email?: string;
  phone?: string;
}

interface CustomerFormProps {
  isUpdate?: boolean;
  isLoading: boolean;
  title: string;
  formFields: CustomerFormFields;
  formErrors: Record<string, any>;
  onSubmit: (data: any) => void;
}

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export default function CustomerForm({
  isUpdate = false,
  isLoading,
  title,
  formFields,
  formErrors,
  onSubmit,
}: CustomerFormProps) {
  const {
    control,
    setValue,
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm<CustomerFormFields>({ mode: 'onBlur' });

  const [isJuridicalType, setIsJuridicalType] = useState(true);
  const [mask, setMask] = useState(getIdentiferMask(PersonType.Juridical));

  useEffect(() => {
    reset(formFields);
    setIsJuridicalType(formFields?.type === PersonType.Juridical);
    setMask(getIdentiferMask(formFields?.type || PersonType.Juridical));
  }, [formFields]);

  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  function typeChanged(value: string) {
    const type = Number(value);
    setIsJuridicalType(PersonType.Juridical === type);
    setMask(getIdentiferMask(type));
  }

  function isIdentifierValid(identifier: string) {
    if (!identifier) {
      return true;
    }
    const value = removeMask(identifier);
    return isJuridicalType ? isCnpjValid(value) : isCpfValid(value);
  }

  return (
    <Form isLoading={isLoading} title={title} onSubmitCallback={() => {}}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='mb-3'>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor='inputLabel' className='form-label'>
            Tipo
          </label>
          <select
            required
            className='form-select'
            aria-label='Selecione o Tipo'
            {...register('type', { required: true })}
            onChange={(e) => typeChanged(e.target.value)}
            disabled={isUpdate}
          >
            <option value=''>Selecione o Tipo</option>
            <option value={PersonType.Juridical}>Pessoa jurídica</option>
            <option value={PersonType.Natural}>Pessoa física</option>
          </select>
          {errors.type && (
            <div className='form-text text-danger'>{errors.type.message}</div>
          )}
        </div>
        <div className='mb-3'>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor='inputLabel' className='form-label'>
            {isJuridicalType ? 'CNPJ' : 'CPF'}
          </label>

          {isUpdate ? (
            <input
              className='form-control'
              type='text'
              value={toIdentifierMask(
                formFields?.identifier || '',
                formFields?.type || PersonType.Juridical
              )}
              disabled
            />
          ) : (
            <IMaskInput
              {...register('identifier', {
                validate: {
                  identifierValid: (value) => {
                    return (
                      isIdentifierValid(value) ||
                      `${isJuridicalType ? 'CNPJ' : 'CPF'} inválido`
                    );
                  },
                },
              })}
              {...{ className: 'form-control', required: true }}
              mask={mask}
              unmask
              onAccept={(value: any) => {
                setValue('identifier', String(value));
              }}
            />
          )}

          {errors.identifier && (
            <div className='form-text text-danger'>
              {errors.identifier?.message}
            </div>
          )}
        </div>

        <div className='mb-3'>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor='inputLabel' className='form-label'>
            {isJuridicalType ? 'Razão Social' : 'Nome'}
          </label>
          <input
            {...register('name', { required: true })}
            className='form-control'
            id='inputLabel'
            placeholder={isJuridicalType ? 'Razão Social' : 'Nome'}
            type='text'
            required
          />
          {errors.name && (
            <div className='form-text text-danger'>{errors.name.message}</div>
          )}
        </div>
        {isJuridicalType && (
          <>
            <div className='mb-3'>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor='inputLabel' className='form-label'>
                Nome Fantasia
              </label>
              <input
                {...register('alias', { required: true })}
                className='form-control'
                id='inputLabel'
                placeholder='Nome Fantasia'
                type='text'
                required
              />
              {errors.alias && (
                <div className='form-text text-danger'>
                  {errors.alias?.message}
                </div>
              )}
            </div>
            <div className='col-sm-6'>
              <div className='form-check form-switch'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  role='switch'
                  id='useAlias'
                  {...register('useAlias')}
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className='form-check-label' htmlFor='useAlias'>
                  Usar Nome Fantasia nas Ordens de Serviço
                </label>
              </div>
            </div>
          </>
        )}

        <div className='mb-3'>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor='email' className='form-label'>
            E-mail
          </label>
          <input
            {...register('email')}
            className='form-control'
            id='email'
            placeholder='E-mail'
            type='email'
          />
          {errors.email && (
            <div className='form-text text-danger'>{errors.email.message}</div>
          )}
        </div>

        <div className='mb-3'>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor='phone' className='form-label'>
            Telefone
          </label>
          <input
            {...register('phone')}
            className='form-control'
            id='phone'
            placeholder='Telefone'
            type='tel'
          />
          {errors.phone && (
            <div className='form-text text-danger'>{errors.phone.message}</div>
          )}
        </div>

        <div className='d-flex justify-content-end gap-2'>
          <Link to='/customers' className='btn btn-secondary'>
            Cancelar
          </Link>
          <button type='submit' className='btn btn-primary'>
            Salvar
          </button>
        </div>
      </form>
    </Form>
  );
}
